import { useEffect, useState, useRef, useMemo } from 'react';
import MarketDataService from '../services/market/MarketDataService';
import { store } from '../redux/store';
import { calcSingleProfit, calcMargin, isBuyOrder } from '../services/platform/TradingStore';

const priceChanged = (prev, next) => {
  if (prev===next) return false;
  if (!prev||!next) return true;
  return prev.bid!==next.bid || prev.ask!==next.ask || prev.high!==next.high || prev.low!==next.low;
};

export const usePriceSubscription = (symbol) => {
  const [price, setPrice] = useState(() => MarketDataService.getPrice(symbol));
  useEffect(() => {
    setPrice(MarketDataService.getPrice(symbol));
    if (!symbol) return;
    return MarketDataService.subscribe(symbol, newP => {
      setPrice(prev => priceChanged(prev, newP) ? newP : prev);
    });
  }, [symbol]);
  return price;
};

export const useMultiplePriceSubscription = (symbols) => {
  const symbolsKey = useMemo(() => symbols?.join(',') || '', [symbols]);
  const [prices, setPrices] = useState(() => {
    const init = {};
    symbols?.forEach(s => { const p = MarketDataService.getPrice(s); if (p) init[s]=p; });
    return init;
  });
  const pricesRef = useRef({});
  const timerRef  = useRef(null);
  useEffect(() => {
    if (!symbols?.length) return;
    const unsubs = symbols.map(s =>
      MarketDataService.subscribe(s, np => {
        if (np) pricesRef.current[s] = np;
        if (!timerRef.current) {
          timerRef.current = setTimeout(() => { setPrices({...pricesRef.current}); timerRef.current=null; }, 100);
        }
      })
    );
    return () => { if (timerRef.current) clearTimeout(timerRef.current); unsubs.forEach(u=>u()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey]);
  return prices;
};

export const useBidPrice  = sym => usePriceSubscription(sym)?.bid  || 0;
export const useAskPrice  = sym => usePriceSubscription(sym)?.ask  || 0;
export const useSpread    = sym => usePriceSubscription(sym)?.spread|| 0;
export const useStaticPrice = (sym, type='bid') => MarketDataService.getPrice(sym)?.[type] || 0;

export const useAllPrices = () => {
  const [prices, setPrices] = useState(() => MarketDataService.getAllPrices());
  const prevRef = useRef(prices);
  useEffect(() => {
    return MarketDataService.subscribeAll(all => {
      const changed = Object.keys(all).some(s => priceChanged(prevRef.current[s], all[s]));
      if (changed) { prevRef.current=all; setPrices(all); }
    });
  }, []);
  return prices;
};

export const useSparkline = (symbol) => {
  const [data, setData] = useState(() => MarketDataService.getPriceHistory(symbol));
  useEffect(() => {
    setData([...MarketDataService.getPriceHistory(symbol)]);
    if (!symbol) return;
    return MarketDataService.subscribe(symbol, () => setData([...MarketDataService.getPriceHistory(symbol)]));
  }, [symbol]);
  return data;
};

export const useMarketDataStatus = () => {
  const [status, setStatus] = useState(() => MarketDataService.getConnectionStatus());
  useEffect(() => {
    const iv = setInterval(() => setStatus(MarketDataService.getConnectionStatus()), 5000);
    return () => clearInterval(iv);
  }, []);
  return status;
};

export const useAccountMetrics = () => {
  const [metrics, setMetrics] = useState({ balance: 0, credit: 0, totalProfit: 0, totalMargin: 0, equity: 0, freeMargin: 0, marginLevel: 0 });
  useEffect(() => {
    const update = () => {
      const s = store.getState();
      const orders = s.platform.positions || [];
      const balance = Number(s.platform.account?.balance || 0);
      const credit = Number(s.platform.account?.credit || 0);
      let totalProfit = 0, totalMargin = 0;
      orders.forEach(o => {
        const p = MarketDataService.getPrice(o.symbol);
        if (p) {
          const cp = isBuyOrder(o) ? p.bid : p.ask;
          totalProfit += calcSingleProfit(o, cp);
        } else {
          totalProfit += Number(o?.profit) || 0;
        }
        totalMargin += calcMargin(o);
      });
      const equity = balance + credit + totalProfit;
      const freeMargin = equity - totalMargin;
      const marginLevel = totalMargin > 0 ? (equity / totalMargin) * 100 : 0;
      setMetrics(prev => {
        const next = {
          balance,
          credit: +credit.toFixed(2),
          totalProfit: +totalProfit.toFixed(2),
          totalMargin: +totalMargin.toFixed(2),
          equity: +equity.toFixed(2),
          freeMargin: +freeMargin.toFixed(2),
          marginLevel: +marginLevel.toFixed(1),
        };
        if (
          prev.balance === next.balance &&
          prev.credit === next.credit &&
          prev.totalProfit === next.totalProfit &&
          prev.totalMargin === next.totalMargin &&
          prev.equity === next.equity &&
          prev.freeMargin === next.freeMargin &&
          prev.marginLevel === next.marginLevel
        ) return prev;
        return next;
      });
    };
    const unsub = MarketDataService.subscribeMetrics(update);
    const unsubStore = store.subscribe(update);
    update();
    return () => { unsub(); unsubStore(); };
  }, []);
  return metrics;
};

export const useInitialPrice = sym => MarketDataService.getPrice(sym);

export default usePriceSubscription;
